import React, { useState, useEffect } from "react";
import { validateTotalAmount, handleAmountChange, handleTotalAmountChange } from "../../helpers";
import { calculateTradeFee } from "../../../Trades/CreateTrade/helpers";
import { convertToNumber, formatAmount } from "./helpers";
import { t } from "../../../../../utils/i18n";

interface Props {
  amount: string;
  tradeType: string;
  canEditAmount?: boolean;
  accountBalance: string;
  feePercent: number;
  setAmount: (val: string) => void;
  updateParent: (key: string, value: string) => void;
}

const AmountInput: React.FC<Props> = ({
  amount,
  tradeType,
  accountBalance,
  setAmount,
  feePercent,
  updateParent,
  canEditAmount = true,
}) => {
  const [amountError, setAmountError] = useState("");
  const [formattedAmount, setFormattedAmount] = useState(
    new Intl.NumberFormat("en-US", {
     style: "decimal",
     maximumFractionDigits: 2,
    }).format(Number(amount) || 0))
  const [fee, setFee] = useState(calculateTradeFee(amount, feePercent));

  useEffect(() => {
    const numericAmount = convertToNumber(formattedAmount);
    setFee(calculateTradeFee(numericAmount.toString(), feePercent));
    setAmount(numericAmount.toString());
    updateParent("amount", numericAmount.toString());
  
  }, [formattedAmount, feePercent, updateParent]);

  const validateAmount = () => {
    const error = validateTotalAmount(
      convertToNumber(formattedAmount).toString(),
      accountBalance,
      tradeType,
      feePercent
    );
    if (error.length > 0) {
      setFormattedAmount("");
    }
    setAmountError(error);
  };

  return (
    <div>
      <div className="rate mt-5">
        <div className="col-md-12 px-0 mb-3">
          {/* Only visible on create offers */}
          <h3 id="createoffer-pricing-title" className={`buy-section_title ${tradeType === "sell" ? "sell-section_title " : ""}`}>
            <p className="createoffers-indexnumber"> 2 </p>
            {" "}
            {t("p2p.amount.set", { verb: tradeType === "sell" ? t("p2p.common.sell") : t("p2p.common.buy") })}
          </h3>
          {/* Only visible on Edit Offers */}
          <h3 id="editoffer-pricing-title" className={`buy-section_title ${tradeType === "sell" ? "sell-section_title " : ""}`}>
            <p className="createoffers-indexnumber"> 2 </p>
            {" "}
            {t("p2p.amount.remaining", { verb: tradeType === "sell" ? t("p2p.common.sell") : t("p2p.common.buy") })}
          </h3>
        </div>
      </div>

      <div className="mt-2 limit-margin">
        <div className="col-md-12 px-0">
          <div className="col-md-12 mt-0 px-0">
            <form className="limit-form createoffer-cont-boxshadow">

              <div id="createoffer-infotext" className="mt-2">
                {tradeType === "sell" && (
                  <p>
                      {t("p2p.amount.exceed")}
                      <b>{" "}
                      {t("p2p.amount.balance", {
                        amount: new Intl.NumberFormat("en-US", {
                          style: "decimal",
                          maximumFractionDigits: 2,
                        }).format(parseFloat(accountBalance)),
                      })}
                    </b>
                  </p>
                )}
              </div>
              <div className="row">
                <div
                  className={tradeType === "sell" ? "col-md-6" : "col-md-12"}
                  title={t("p2p.amount.tip", { verb: tradeType === "sell" ? t("p2p.common.verb_sell") : t("p2p.common.verb_buy") })}
                >
                  <label className="createoffer-sublabels">
                    {t("p2p.amount.total")} <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="input-group mb-1">
                    <input
                      id="pricing-totalamount-input"
                      type="text"
                      className="createoffer-input"
                      placeholder="0.00"
                      disabled={!canEditAmount}
                      value={formattedAmount}
                      autoFocus
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)./g, '$1');
                        handleTotalAmountChange(e, setAmount, setFormattedAmount,
                         );
                        setAmountError("");
                      }}
                      onKeyUp={() => validateAmount()}
                    />
                    <div className="createoffer-currency-code-tag">
                      <p>USD</p>
                    </div>
                  </div>
                  {!canEditAmount && (
                    <p>
                      {t("p2p.amount.locked")}
                    </p>
                  )}
                  {amountError && (
                    <div
                      className="createoffer-error-alert"
                    >
                      {amountError}
                    </div>
                  )}
                </div>
                {tradeType === "sell" && (
                  <div className="col-md-6" title={t("p2p.amount.fee_tip")}>
                    <label className="createoffer-sublabels">
                    <i className="fas fa-question-circle"></i>{" "} 
                    {t("p2p.amount.fee")} <span style={{ color: "red" }}>*</span>
                    </label>
                    <div className="input-group mb-1">
                      <input
                        readOnly
                        type="text"
                        className="createoffer-input fee"
                        placeholder="0.00"
                        value={
                          fee
                            ? new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              }).format(fee)
                            : "0.00"
                        }
                      />
                      <div className="createoffer-currency-code-tag">
                        <p>USD</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmountInput;
